/**
 * SATEL equipment catalog — verified entries from SATEL 2026 product
 * catalog (MD-KATALOG-PL IND_0226, 111 pages, official Polish edition).
 *
 * Every spec below is directly cited from the catalog text. Where a value
 * wasn't on the page (e.g. exact mounting height range for some legacy PIRs)
 * the field is left undefined rather than guessed. This is by-design per
 * the project rule: "tylko zweryfikowane faktyczne dane".
 *
 * Phase 1 selection: ~30 most-installed SKUs covering ~80% of typical
 * PL/CEE intrusion + fire alarm projects. Rest of SATEL portfolio (~170
 * additional SKUs in the catalog) intentionally deferred — adding them
 * later is mechanical once the picker UX is validated.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

const SATEL_CATALOG_URL = 'https://www.satel.pl/api/download/leaflet/551';

export const SATEL_EQUIPMENT_CATALOG: EquipmentCatalogEntry[] = [
  // ── Outdoor wall PIR + dual-tech ──────────────────────────────────────
  {
    id: 'satel-opal-pro',
    manufacturer: 'SATEL',
    model: 'OPAL Pro',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 15,
    hfovDeg: 97,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 2.4,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true, // Active IR
    petImmune: true,   // up to 20 kg
    ipRating: 'IP54',
    tagline: 'Outdoor PIR+MW dual-tech, EN 50131 G3 indoor / G2 outdoor, active IR anti-masking',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },
  {
    id: 'satel-opal-plus',
    manufacturer: 'SATEL',
    model: 'OPAL Plus',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 15, // catalog: "exceeds 15 m"
    hfovDeg: 97,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 2.4,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true, // MW anti-masking
    petImmune: true,
    ipRating: 'IP54',
    tagline: 'Outdoor PIR+MW dual-tech, EN 50131 G2, MW anti-masking, dusk sensor',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },
  {
    id: 'satel-opal',
    manufacturer: 'SATEL',
    model: 'OPAL',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 15, // same family as Plus / Pro per catalog
    hfovDeg: 97,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 2.4,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    petImmune: true,
    ipRating: 'IP54',
    tagline: 'Outdoor PIR+MW dual-tech, EN 50131 G2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },

  // ── Indoor wall PIR (SLIM LINE) ───────────────────────────────────────
  {
    id: 'satel-slim-pir-pro',
    manufacturer: 'SATEL',
    model: 'SLIM-PIR-PRO',
    category: 'outdoor-wall-pir', // wall-mount despite category name (legacy)
    pattern: 'cone',
    rangeM: 20, // 20×24 m with WD lens
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    antiMasking: true, // Active IR per EN 50131-2-2
    tagline: 'Indoor PIR, EN 50131 G3, active IR anti-masking, 3 lens options (WD/LR/CT)',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },
  {
    id: 'satel-slim-dual-pro',
    manufacturer: 'SATEL',
    model: 'SLIM-DUAL-PRO',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 20,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true, // Active IR EN 50131-2-4
    tagline: 'Indoor PIR+MW dual-tech, EN 50131 G3, active IR anti-masking',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },
  {
    id: 'satel-grey-plus',
    manufacturer: 'SATEL',
    model: 'GREY Plus',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 13, // 13×11 m
    hfovDeg: 104,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true, // MW anti-masking
    petImmune: true, // up to 15 kg
    tagline: 'Indoor PIR+MW dual-tech, MW anti-masking, pet immune to 15 kg',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },
  {
    id: 'satel-graphite',
    manufacturer: 'SATEL',
    model: 'GRAPHITE',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 18,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true, // option up to 15 kg
    tagline: 'Indoor PIR, EN 50131 G2, replaceable Fresnel lens, pet option to 15 kg',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },
  {
    id: 'satel-aqua-plus',
    manufacturer: 'SATEL',
    model: 'AQUA Plus',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 15, // 15×14 m
    hfovDeg: 130,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    tagline: 'Indoor PIR, EN 50131 G2, 130° wide angle, replaceable Fresnel lens',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },
  {
    id: 'satel-aqua-pet',
    manufacturer: 'SATEL',
    model: 'AQUA Pet',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 18, // 18×16 m
    hfovDeg: 90,
    mountingHeightDefaultM: 2.3,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true, // up to 15 kg
    tagline: 'Indoor PIR, EN 50131 G2, pet immune to 15 kg',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },

  // ── Ceiling 360° PIR ──────────────────────────────────────────────────
  {
    id: 'satel-aqua-ring',
    manufacturer: 'SATEL',
    model: 'AQUA Ring',
    category: 'indoor-ceiling-pir',
    pattern: 'full',
    rangeM: 6, // approximate radius at 2.7m mount, catalog doesn't pin a ceilingDiameterByHeight
    hfovDeg: 360,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 3.6,
    mounting: ['ceiling'],
    detectionMethod: 'pir',
    tagline: 'Ceiling 360° PIR, EN 50131 G2, quad pyroelement',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },

  // ── Outdoor curtain (AGATE) ───────────────────────────────────────────
  {
    id: 'satel-agate',
    manufacturer: 'SATEL',
    model: 'AGATE',
    category: 'curtain-pir',
    pattern: 'curtain',
    rangeM: 14, // 14×0.7 m
    hfovDeg: 5,
    mountingHeightDefaultM: 1.5,
    mountingHeightMinM: 1.2,
    mountingHeightMaxM: 2.5,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    ipRating: 'IP54',
    tagline: 'Outdoor curtain PIR+MW, EN 50131 G3 indoor, active IR anti-masking',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },
  {
    id: 'satel-cd-2',
    manufacturer: 'SATEL',
    model: 'CD-2',
    category: 'curtain-pir',
    pattern: 'curtain',
    rangeM: 5, // 5 m × 1 m
    hfovDeg: 15,
    mountingHeightDefaultM: 1.5,
    mountingHeightMinM: 1.0,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir',
    tagline: 'Indoor curtain PIR, 5×1 m / 15° narrow zone',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },

  // ── Beam barriers (ACTIVA series) ─────────────────────────────────────
  // Per catalog: same electronics, different beam counts. Range 20 m
  // indoor / 10 m outdoor for the entire family. IP44.
  {
    id: 'satel-activa-2',
    manufacturer: 'SATEL',
    model: 'ACTIVA-2',
    category: 'beam-barrier',
    pattern: 'beam',
    rangeM: 20, // indoor max; outdoor 10 m
    hfovDeg: 0,
    mountingHeightDefaultM: 0.5,
    mountingHeightMinM: 0.3,
    mountingHeightMaxM: 1.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP44',
    tagline: '2-beam infrared barrier, 52 cm bar, 20 m indoor / 10 m outdoor',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },
  {
    id: 'satel-activa-4',
    manufacturer: 'SATEL',
    model: 'ACTIVA-4',
    category: 'beam-barrier',
    pattern: 'beam',
    rangeM: 20,
    hfovDeg: 0,
    mountingHeightDefaultM: 0.7,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP44',
    tagline: '4-beam infrared barrier, 105 cm bar, 20 m indoor / 10 m outdoor',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },
  {
    id: 'satel-activa-6',
    manufacturer: 'SATEL',
    model: 'ACTIVA-6',
    category: 'beam-barrier',
    pattern: 'beam',
    rangeM: 20,
    hfovDeg: 0,
    mountingHeightDefaultM: 0.9,
    mountingHeightMinM: 0.7,
    mountingHeightMaxM: 1.5,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP44',
    tagline: '6-beam infrared barrier, 158 cm bar, 20 m indoor / 10 m outdoor',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
  },

  // ── Magnetic contacts (kontaktrony) ───────────────────────────────────
  {
    id: 'satel-k-1',
    manufacturer: 'SATEL',
    model: 'K-1',
    category: 'magnetic-contact',
    tagline: 'Surface-mount magnetic contact, plastic housing',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: false,
    },
  },
  {
    id: 'satel-k-1-2e',
    manufacturer: 'SATEL',
    model: 'K-1 2E',
    category: 'magnetic-contact',
    tagline: 'Surface-mount magnetic contact, plastic, built-in 2EOL 1.1 kΩ',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      builtIn2EOL: true,
      tamper: false,
    },
  },
  {
    id: 'satel-k-2',
    manufacturer: 'SATEL',
    model: 'K-2',
    category: 'magnetic-contact',
    tagline: 'Recessed magnetic contact, plastic housing',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      mountType: 'recessed',
      housing: 'plastic',
      tamper: false,
    },
  },
  {
    id: 'satel-s-4',
    manufacturer: 'SATEL',
    model: 'S-4',
    category: 'magnetic-contact',
    tagline: 'Surface-mount magnetic contact, EN 50131 G2 with tamper contact',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
    },
  },
  {
    id: 'satel-b-4m',
    manufacturer: 'SATEL',
    model: 'B-4M',
    category: 'magnetic-contact',
    tagline: 'Heavy-duty gate / garage door contact, metal hermetic housing',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      mountType: 'gate',
      housing: 'metal-hermetic',
      gateRated: true,
      tamper: false,
      dimensionsMm: '106×36.5×10',
    },
  },
  {
    id: 'satel-b-4l',
    manufacturer: 'SATEL',
    model: 'B-4L',
    category: 'magnetic-contact',
    tagline: 'Heavy-duty gate / garage door contact, larger air gap',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      mountType: 'gate',
      housing: 'metal-hermetic',
      gateRated: true,
      tamper: false,
      dimensionsMm: '104.5×44.5×11.5',
    },
  },

  // ── Smoke / fire / CO detectors ───────────────────────────────────────
  {
    id: 'satel-tsd-1',
    manufacturer: 'SATEL',
    model: 'TSD-1',
    category: 'smoke-detector',
    tagline: 'Universal smoke + heat detector, EN 54-7 + EN 54-5, Swirl chamber',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      detection: ['smoke-optical', 'heat-fixed', 'multi'],
      standard: ['EN 54-7', 'EN 54-5'],
      standalone: false,
    },
  },
  {
    id: 'satel-sda-250',
    manufacturer: 'SATEL',
    model: 'SDA-250',
    category: 'smoke-detector',
    tagline: 'Standalone photoelectric smoke, EN 14604, 10-year battery, Swirl chamber',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      detection: ['smoke-optical'],
      standard: ['EN 14604'],
      standalone: true,
      batteryYears: 10,
    },
  },
  {
    id: 'satel-asd-250',
    manufacturer: 'SATEL',
    model: 'ASD-250',
    category: 'smoke-detector',
    tagline: 'Wireless photoelectric smoke, EN 14604, ABAX 2 / BE WAVE compatible',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      detection: ['smoke-optical'],
      standard: ['EN 14604'],
      standalone: false,
      wirelessSystem: 'ABAX 2',
    },
  },
  {
    id: 'satel-msd-350',
    manufacturer: 'SATEL',
    model: 'MSD-350',
    category: 'smoke-detector',
    tagline: 'Wireless photoelectric smoke, EN 14604, MICRA compatible',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      detection: ['smoke-optical'],
      standard: ['EN 14604'],
      standalone: false,
      wirelessSystem: 'MICRA',
    },
  },
  {
    id: 'satel-acmd-200',
    manufacturer: 'SATEL',
    model: 'ACMD-200',
    category: 'smoke-detector',
    tagline: 'Wireless CO (carbon monoxide) detector, ABAX 2 compatible',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      detection: ['co'],
      standalone: false,
      wirelessSystem: 'ABAX 2',
    },
  },

  // ── Glass-break detectors ─────────────────────────────────────────────
  {
    id: 'satel-magenta',
    manufacturer: 'SATEL',
    model: 'MAGENTA',
    category: 'glass-break',
    tagline: 'Dual-channel digital glass-break, plain / tempered / laminated glass',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      method: 'acoustic-dual',
      glassTypes: ['plain', 'tempered', 'laminated'],
    },
  },
  {
    id: 'satel-indigo',
    manufacturer: 'SATEL',
    model: 'INDIGO',
    category: 'glass-break',
    tagline: 'Advanced acoustic glass-break, microprocessor dual-channel analysis',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      method: 'acoustic-dual',
      glassTypes: ['plain', 'tempered', 'laminated'],
    },
  },

  // ── Outdoor sirens ────────────────────────────────────────────────────
  {
    id: 'satel-sp-6500-r',
    manufacturer: 'SATEL',
    model: 'SP-6500 R',
    category: 'outdoor-siren',
    tagline: 'Outdoor optic+acoustic siren, EN 50131 G2, foam-fill protection optional',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      grade: 2,
      batteryBackup: 'socket',
      antiFoamTamper: false, // optional SX6-TO
    },
  },
  {
    id: 'satel-sp-4004-r',
    manufacturer: 'SATEL',
    model: 'SP-4004 R',
    category: 'outdoor-siren',
    tagline: 'Outdoor optic+acoustic siren, EN 50131 G2, hermetic electronics',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      grade: 2,
      batteryBackup: 'socket',
    },
  },
  {
    id: 'satel-sd-6000-r',
    manufacturer: 'SATEL',
    model: 'SD-6000 R',
    category: 'outdoor-siren',
    tagline: 'Outdoor dynamic-transducer siren, EN 50131 G2, anti-foam tamper optional',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      acoustic: 'dynamic',
      optical: 'led',
      grade: 2,
      batteryBackup: 'socket',
      antiFoamTamper: true,
    },
  },

  // ── Indoor sounders ───────────────────────────────────────────────────
  {
    id: 'satel-spw-210',
    manufacturer: 'SATEL',
    model: 'SPW-210',
    category: 'indoor-sounder',
    tagline: 'Indoor piezo sounder, EN 50131 G2',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      acoustic: 'piezo',
      optical: 'none',
      grade: 2,
    },
  },
  {
    id: 'satel-spw-220',
    manufacturer: 'SATEL',
    model: 'SPW-220',
    category: 'indoor-sounder',
    tagline: 'Indoor piezo + LED optical sounder',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      acoustic: 'piezo',
      optical: 'led',
    },
  },
  {
    id: 'satel-spw-250',
    manufacturer: 'SATEL',
    model: 'SPW-250',
    category: 'indoor-sounder',
    tagline: 'Indoor piezo sounder, lithium battery backup',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      acoustic: 'piezo',
      batteryBackup: 'built-in',
    },
  },

  // ── Panic buttons ─────────────────────────────────────────────────────
  {
    id: 'satel-pnk-1',
    manufacturer: 'SATEL',
    model: 'PNK-1',
    category: 'panic-button',
    tagline: 'Panic button with mechanical memory latch, NC contact',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      memory: 'mechanical-latch',
      connection: 'wired-nc',
    },
  },
  {
    id: 'satel-pb-10',
    manufacturer: 'SATEL',
    model: 'PB-10',
    category: 'panic-button',
    tagline: 'Basic panic button, NC contact, accidental-press protection',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      memory: 'momentary',
      connection: 'wired-nc',
      accidentalPressProtection: true,
    },
  },
  {
    id: 'satel-mpb-300',
    manufacturer: 'SATEL',
    model: 'MPB-300',
    category: 'panic-button',
    tagline: 'Wireless panic button, MICRA / RK system, tamper protection',
    verified: true,
    datasheetUrl: SATEL_CATALOG_URL,
    specs: {
      memory: 'momentary',
      connection: 'wireless',
      wirelessSystem: 'MICRA',
      accidentalPressProtection: true,
      tamper: true,
    },
  },
];
